import { Box, Button, TextField, Typography, Autocomplete } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { theme } from 'app/Theme';

/**
 * @typedef {Object} FilterConfig
 * @property {string}   id        - Unique filter key.
 * @property {string}   label     - Displayed label inside the input.
 * @property {string}   [placeholder] - Placeholder text.
 * @property {Array}    options   - Array of { value, label } objects.
 * @property {Object|null} value  - Currently selected option object (or null).
 * @property {Function} onChange  - Called with the selected option object (or null).
 * @property {number}   [minWidth] - Min-width of the dropdown (default 130).
 */

const autocompletePaperSx = {
    paper: {
        sx: {
            '& .MuiAutocomplete-option': {
                fontSize: '12px'
            }
        }
    }
};

const autocompleteInputSx = {
    '& .MuiInputBase-input': { fontSize: '12px' },
    '& .MuiInputLabel-root': { fontSize: '12px' }
};

/**
 * TableFilterBar – right-aligned strip of typeable Autocomplete filter dropdowns.
 *
 * @param {Object}         props
 * @param {FilterConfig[]} props.filters              - Filter configurations.
 * @param {Function}       props.onResetFilters       - Called when the Reset button is clicked.
 * @param {React.Node}     [props.extraFiltersContent] - Additional custom filter components
 *   (e.g. async Redux-connected Select dropdowns) rendered between the label and the reset button.
 */
const TableFilterBar = ({ filters = [], onResetFilters, extraFiltersContent }) => {
    if (!filters.length && !extraFiltersContent) return null;

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                flexWrap: 'wrap'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    ml: 'auto'
                }}
            >
                <Typography
                    variant='p'
                    sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        mr: 1
                    }}
                >
                    Filters:
                </Typography>

                {extraFiltersContent}

                {filters.map((filter) => (
                    <Autocomplete
                        key={filter.id}
                        size='small'
                        sx={{
                            minWidth: filter.minWidth || 130,
                            backgroundColor: (t) => t.palette.common.paper
                        }}
                        componentsProps={autocompletePaperSx}
                        options={filter.options || []}
                        getOptionLabel={(opt) => opt.label || ''}
                        value={filter.value || null}
                        onChange={(_, newValue) => filter.onChange(newValue)}
                        isOptionEqualToValue={(option, value) =>
                            option.value === value?.value
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={filter.label}
                                placeholder={filter.placeholder || `Select ${filter.label.toLowerCase()}`}
                                sx={autocompleteInputSx}
                            />
                        )}
                    />
                ))}

                <Button
                    variant='outlined'
                    onClick={onResetFilters}
                    startIcon={<RefreshIcon />}
                    size='small'
                    sx={{ height: '34px', minHeight: '34px' }}
                >
                    Reset
                </Button>
            </Box>
        </Box>
    );
};

export default TableFilterBar;
